import React from "react";
import { Badge, IconButton, Tooltip } from "@mui/material";
import { getPhaseColor, getStatusLabel, StatusPhase } from "../helper";
import { STATUS_TOOLTIP_PROPS } from "../../../components/DownloadMenu/MenuPopover";

interface StatusIconProps {
    phase: StatusPhase;
    needsAction: boolean;
    tooltip: string;
    icon: React.ReactNode;
    onClick?: () => void;
    verifiedBadge?: boolean;
    count?: number;
    label?: string;
    /** Accessible name for the icon button (screen readers, tests). */
    ariaLabel?: string;
}

const StatusIcon: React.FC<StatusIconProps> = ({ phase, needsAction, tooltip, icon, onClick, verifiedBadge, count, label, ariaLabel }) => {
    const color = getPhaseColor(phase);
    const showBadge = needsAction || !!verifiedBadge;
    const badgeContent = needsAction ? "!" : "✓";
    const showCount = typeof count === "number" && count > 0;

    const tooltipContent = (
        <div style={{ padding: "2px 1px" }}>
            <div style={{ fontWeight: 700, fontSize: 12.5, color, marginBottom: 3 }}>
                {getStatusLabel(phase)}
            </div>
            <div style={{ fontSize: 11.5, lineHeight: 1.4, color: "var(--text-2)" }}>{tooltip}</div>
        </div>
    );

    return (
        <Tooltip
            title={tooltipContent}
            arrow
            componentsProps={STATUS_TOOLTIP_PROPS}
        >
            <span style={{ display: "inline-flex", position: "relative" }}>
                <Badge
                    badgeContent={showBadge ? badgeContent : undefined}
                    color="success"
                    overlap="circular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    invisible={!showBadge}
                    sx={{
                        "& .MuiBadge-badge": {
                            width: 16,
                            height: 16,
                            minWidth: 16,
                            fontSize: 10,
                            fontWeight: 900,
                            padding: 0,
                            border: "1.5px solid var(--surface)",
                            ...(needsAction && { backgroundColor: color, color: "#fff" }),
                        },
                    }}
                >
                    <IconButton
                        size="small"
                        aria-label={ariaLabel}
                        onClick={onClick}
                        sx={{
                            color,
                            padding: "8px",
                            "&:hover": { backgroundColor: `${color}18` },
                        }}
                    >
                        {icon}
                    </IconButton>
                </Badge>
                {showCount && (
                    <span
                        style={{
                            position: "absolute",
                            bottom: 1,
                            right: 0,
                            fontSize: 11,
                            fontWeight: 800,
                            lineHeight: 1,
                            color,
                            textShadow: "0 0 2px var(--surface), 0 0 2px var(--surface)",
                            pointerEvents: "none",
                        }}
                    >
                        {count}
                    </span>
                )}
                {label && (
                    <span
                        style={{
                            position: "absolute",
                            bottom: 0,
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: 6,
                            fontWeight: 800,
                            letterSpacing: 0.3,
                            lineHeight: 1,
                            color,
                            textShadow: "0 0 2px var(--surface), 0 0 2px var(--surface)",
                            pointerEvents: "none",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {label}
                    </span>
                )}
            </span>
        </Tooltip>
    );
};

export default StatusIcon;
